import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addDocument,
  getCompanyDocuments,
  update,
} from "../../../services/firestoreServices";
import { alert as showAlert } from "../../../utils/alert";
import { Folder } from "../types";

interface UseFolderManagementProps {
  existingFolders: Folder[];
  parentFolderId?: string;
}

export function useFolderManagement({
  existingFolders,
  parentFolderId,
}: UseFolderManagementProps) {
  const { t } = useTranslation();
  const [dialogVisible, setDialogVisible] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [error, setError] = useState("");
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);

  const openCreateDialog = () => {
    setFolderName("");
    setError("");
    setEditingFolder(null);
    setDialogVisible(true);
  };

  const openEditDialog = (folder: Folder) => {
    setFolderName(folder.name);
    setError("");
    setEditingFolder(folder);
    setDialogVisible(true);
  };

  const closeNameDialog = () => {
    setDialogVisible(false);
    setFolderName("");
    setError("");
    setEditingFolder(null);
  };

  const handleNameChange = (name: string) => {
    setFolderName(name);
    setError("");
  };

  const saveFolder = async () => {
    const normalizedName = folderName.trim();

    if (!normalizedName) {
      setError("Folder name is required");
      return;
    }

    const folderExists = existingFolders.some(
      (folder) =>
        folder.id !== editingFolder?.id &&
        folder.name.toLowerCase() === normalizedName.toLowerCase(),
    );

    if (folderExists) {
      setError("A folder with this name already exists");
      return;
    }

    try {
      if (editingFolder) {
        const now = new Date();

        await update("drive_folders", editingFolder.id, {
          name: normalizedName,
          updatedAt: now,
        });
      } else {
        const now = new Date();

        await addDocument(
          { name: "drive_folders" },
          {
            name: normalizedName,
            icon: "folder",
            iconColor: "#2563eb",
            fileCount: 0,
            size: "0 MB",
            createdAt: now,
            updatedAt: now,
            ...(parentFolderId && { parentFolderId }),
            deleted: false,
          },
        );
      }

      closeNameDialog();
    } catch (error) {
      console.error("Failed to save folder:", error);
      setError("Failed to save folder. Please try again.");
    }
  };

  const openDeleteDialog = (folder: Folder) => {
    showAlert(
      t("Delete Folder"),
      t("Are you sure you want to delete this folder?"),
      [
        {
          text: t("Cancel"),
          style: "cancel",
        },
        {
          text: t("Delete"),
          style: "destructive",
          onPress: () => {
            void deleteFolder(folder);
          },
        },
      ],
    );
  };

  const deleteFolder = async (folderToDelete: Folder) => {
    if (!folderToDelete) return;

    try {
      const [subFolders, files] = await Promise.all([
        getCompanyDocuments("drive_folders", {
          where: [
            {
              field: "parentFolderId",
              operator: "==",
              value: folderToDelete.id,
            },
          ],
          limit: 1,
        }),
        getCompanyDocuments("drive_files", {
          where: [
            {
              field: "folderId",
              operator: "==",
              value: folderToDelete.id,
            },
          ],
          limit: 1,
        }),
      ]);

      if (subFolders.length > 0 || files.length > 0) {
        showAlert(
          t("Delete Folder"),
          t("Folder is not empty. Remove its contents first."),
          [{ text: t("OK") }],
        );
        return;
      }

      await update("drive_folders", folderToDelete.id, {
        deleted: true,
        updatedAt: new Date(),
      });
    } catch (error) {
      console.error("Failed to delete folder:", error);
      showAlert(
        t("Error"),
        t("Failed to delete folder. Please try again."),
        [{ text: t("OK") }],
      );
    }
  };

  return {
    dialogVisible,
    folderName,
    error,
    editingFolder,
    openCreateDialog,
    openEditDialog,
    openDeleteDialog,
    closeNameDialog,
    handleNameChange,
    saveFolder,
  };
}
